/**
 * Exam prep API endpoints (Phase 07).
 */

import { fetchApi } from '../core';
import type {
  AssignmentTagResponse,
  AttachCourseExamProfileData,
  CourseExamProfileResponse,
  CreateExamProfileData,
  CreateRubricTemplateData,
  CreateScoreConversionProfileData,
  CreateSkillTaxonomyData,
  ExamProfileResponse,
  RubricTemplateResponse,
  ScoreConversionProfileResponse,
  SkillTaxonomyResponse,
  TagAssignmentData,
  UpdateExamProfileData,
  UpdateRubricTemplateData,
  UpdateScoreConversionProfileData,
  UpdateSkillTaxonomyData,
} from '../types';

function listQs(includePlatformTemplates: boolean): string {
  return includePlatformTemplates ? '?include_platform_templates=true' : '';
}

export function listExamProfiles(
  includePlatformTemplates: boolean = false,
  componentName: string = 'Unknown'
) {
  return fetchApi<ExamProfileResponse[]>(
    `/exam-profiles${listQs(includePlatformTemplates)}`,
    {},
    componentName
  );
}

export function createExamProfile(
  data: CreateExamProfileData,
  componentName: string = 'Unknown'
) {
  return fetchApi<ExamProfileResponse>(
    '/exam-profiles',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateExamProfile(
  profileId: string,
  data: UpdateExamProfileData,
  componentName: string = 'Unknown'
) {
  return fetchApi<ExamProfileResponse>(
    `/exam-profiles/${profileId}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function deleteExamProfile(
  profileId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<{ message: string }>(
    `/exam-profiles/${profileId}`,
    { method: 'DELETE' },
    componentName
  );
}

export function getCourseExamProfile(
  courseId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<CourseExamProfileResponse | { course_exam_profile: null }>(
    `/courses/${courseId}/exam-profile`,
    {},
    componentName
  );
}

export function attachCourseExamProfile(
  courseId: string,
  data: AttachCourseExamProfileData,
  componentName: string = 'Unknown'
) {
  return fetchApi<CourseExamProfileResponse>(
    `/courses/${courseId}/exam-profile`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function detachCourseExamProfile(
  courseId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<{ message: string }>(
    `/courses/${courseId}/exam-profile`,
    { method: 'DELETE' },
    componentName
  );
}

export function listSkillTaxonomies(
  includePlatformTemplates: boolean = false,
  componentName: string = 'Unknown'
) {
  return fetchApi<SkillTaxonomyResponse[]>(
    `/skill-taxonomies${listQs(includePlatformTemplates)}`,
    {},
    componentName
  );
}

export function createSkillTaxonomy(
  data: CreateSkillTaxonomyData,
  componentName: string = 'Unknown'
) {
  return fetchApi<SkillTaxonomyResponse>(
    '/skill-taxonomies',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateSkillTaxonomy(
  taxonomyId: string,
  data: UpdateSkillTaxonomyData,
  componentName: string = 'Unknown'
) {
  return fetchApi<SkillTaxonomyResponse>(
    `/skill-taxonomies/${taxonomyId}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function deleteSkillTaxonomy(
  taxonomyId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<{ message: string }>(
    `/skill-taxonomies/${taxonomyId}`,
    { method: 'DELETE' },
    componentName
  );
}

export function listRubricTemplates(
  includePlatformTemplates: boolean = false,
  componentName: string = 'Unknown'
) {
  return fetchApi<RubricTemplateResponse[]>(
    `/rubric-templates${listQs(includePlatformTemplates)}`,
    {},
    componentName
  );
}

export function createRubricTemplate(
  data: CreateRubricTemplateData,
  componentName: string = 'Unknown'
) {
  return fetchApi<RubricTemplateResponse>(
    '/rubric-templates',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateRubricTemplate(
  templateId: string,
  data: UpdateRubricTemplateData,
  componentName: string = 'Unknown'
) {
  return fetchApi<RubricTemplateResponse>(
    `/rubric-templates/${templateId}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function deleteRubricTemplate(
  templateId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<{ message: string }>(
    `/rubric-templates/${templateId}`,
    { method: 'DELETE' },
    componentName
  );
}

export function listScoreConversionProfiles(
  includePlatformTemplates: boolean = false,
  componentName: string = 'Unknown'
) {
  return fetchApi<ScoreConversionProfileResponse[]>(
    `/score-conversion-profiles${listQs(includePlatformTemplates)}`,
    {},
    componentName
  );
}

export function createScoreConversionProfile(
  data: CreateScoreConversionProfileData,
  componentName: string = 'Unknown'
) {
  return fetchApi<ScoreConversionProfileResponse>(
    '/score-conversion-profiles',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateScoreConversionProfile(
  profileId: string,
  data: UpdateScoreConversionProfileData,
  componentName: string = 'Unknown'
) {
  return fetchApi<ScoreConversionProfileResponse>(
    `/score-conversion-profiles/${profileId}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function deleteScoreConversionProfile(
  profileId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<{ message: string }>(
    `/score-conversion-profiles/${profileId}`,
    { method: 'DELETE' },
    componentName
  );
}

export function listAssignmentTags(
  assignmentId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<AssignmentTagResponse[]>(
    `/assignments/${assignmentId}/tags`,
    {},
    componentName
  );
}

export function tagAssignment(
  assignmentId: string,
  data: TagAssignmentData,
  componentName: string = 'Unknown'
) {
  return fetchApi<AssignmentTagResponse>(
    `/assignments/${assignmentId}/tags`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function deleteAssignmentTag(
  tagId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<{ message: string }>(
    `/assignment-tags/${tagId}`,
    { method: 'DELETE' },
    componentName
  );
}
